import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  Shield, LogOut, RefreshCw, Users, Eye, Star, DollarSign,
  Globe, Pin, PinOff, Trash2, Plus, Minus, Check, Crown, Loader2, Code2, ExternalLink,
  Sparkles, Zap, AlertTriangle, Power,
} from "lucide-react";
import { toast, Toaster } from "sonner";
import AdminFunnel from "@/components/AdminFunnel";
import AdminPackages from "@/components/AdminPackages";
import { disableTracking } from "@/lib/track";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => (n ?? 0).toLocaleString("ar-SA");
const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("ar-SA", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
};

const TABS = [
  { id: "overview", label: "النظرة العامة", icon: Eye },
  { id: "visits", label: "الزيارات", icon: Globe },
  { id: "users", label: "المستخدمون", icon: Users },
  { id: "reviews", label: "التقييمات", icon: Star },
  { id: "embeds", label: "مواقع التضمين", icon: Code2 },
  { id: "ai", label: "الذكاء الاصطناعي", icon: Sparkles },
];

export default function AdminDashboard({ initialTab = "overview" }) {
  const navigate = useNavigate();
  const [key, setKey] = useState(() => sessionStorage.getItem("pl_admin_key") || "");
  const [authed, setAuthed] = useState(() => !!sessionStorage.getItem("pl_admin_key"));
  const [tab, setTab] = useState(initialTab);
  const [loading, setLoading] = useState(false);

  // No in-dashboard login screen — if the key is missing, redirect to the
  // main site and open the AccountModal in admin mode via ?signin=admin.
  useEffect(() => {
    if (!authed) navigate("/?signin=admin", { replace: true });
  }, [authed, navigate]);

  const headers = useMemo(() => ({ "X-Admin-Key": key }), [key]);

  const [stats, setStats] = useState(null);
  const [visits, setVisits] = useState([]);
  const [users, setUsers] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [embedHosts, setEmbedHosts] = useState([]);
  const [filter, setFilter] = useState("all");

  // AI tab state
  const [aiOverview, setAiOverview] = useState(null);
  const [aiUsageDays, setAiUsageDays] = useState([]);
  const [aiUsageList, setAiUsageList] = useState([]);
  const [aiCache, setAiCache] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);

  const loadAI = async (k = key) => {
    if (!k) return;
    setAiLoading(true);
    try {
      const H = { "X-Admin-Key": k };
      const [o, d, u, c] = await Promise.all([
        axios.get(`${API}/admin/ai/overview`, { headers: H }),
        axios.get(`${API}/admin/ai/usage/days?limit=30`, { headers: H }),
        axios.get(`${API}/admin/ai/usage?limit=30`, { headers: H }),
        axios.get(`${API}/admin/ai/cache?limit=50`, { headers: H }),
      ]);
      setAiOverview(o.data);
      setAiUsageDays(d.data.items || []);
      setAiUsageList(u.data.items || []);
      setAiCache(c.data.items || []);
    } catch {
      toast.error("تعذّر تحميل بيانات AI");
    } finally {
      setAiLoading(false);
    }
  };

  const saveAiSettings = async (patch) => {
    try {
      const r = await axios.post(`${API}/admin/ai/settings`, patch, { headers });
      setAiOverview((prev) => (prev ? { ...prev, settings: r.data } : prev));
      toast.success("تم الحفظ");
    } catch {
      toast.error("تعذّر الحفظ");
    }
  };

  const clearAiCache = async () => {
    if (!window.confirm("مسح كل بيانات ذاكرة الـ AI؟ الطلبات القادمة ستستدعي Claude من جديد.")) return;
    try {
      const r = await axios.delete(`${API}/admin/ai/cache`, { headers });
      toast.success(`تم مسح ${r.data.deleted} عنصر`);
      loadAI();
    } catch {
      toast.error("فشل المسح");
    }
  };

  const deleteCacheEntry = async (id) => {
    try {
      await axios.delete(`${API}/admin/ai/cache/${id}`, { headers });
      setAiCache((prev) => prev.filter((x) => x.id !== id));
      toast.success("تم الحذف");
    } catch {
      toast.error("فشل الحذف");
    }
  };

  const toggleEmbedBlock = async (host, nextBlocked) => {
    try {
      await axios.post(
        `${API}/admin/embed/hosts/${encodeURIComponent(host)}/block?blocked=${nextBlocked ? "true" : "false"}`,
        {},
        { headers },
      );
      setEmbedHosts((prev) => prev.map((x) => (x.host === host ? { ...x, blocked: nextBlocked } : x)));
      toast.success(nextBlocked ? "تم حظر الموقع" : "تم إلغاء الحظر");
    } catch {
      toast.error("تعذّر تحديث حالة الحظر");
    }
  };

  const deleteEmbedHost = async (host) => {
    if (!window.confirm(`حذف تسجيل ${host}؟ (لن يمنع الموقع من إعادة تضمين الحاسبة لاحقًا — استخدم "حظر" لمنعه).`)) return;
    try {
      await axios.delete(`${API}/admin/embed/hosts/${encodeURIComponent(host)}`, { headers });
      setEmbedHosts((prev) => prev.filter((x) => x.host !== host));
      toast.success("تم الحذف");
    } catch {
      toast.error("فشل الحذف");
    }
  };

  // Auto-load AI data when switching to the AI tab for the first time.
  useEffect(() => {
    if (tab === "ai" && authed && !aiOverview) loadAI();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, authed]);

  const loadAll = async (k = key) => {
    if (!k) return;
    setLoading(true);
    try {
      const [s, v, u, f, eh] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers: { "X-Admin-Key": k } }),
        axios.get(`${API}/admin/visits/days?limit=30`, { headers: { "X-Admin-Key": k } }),
        axios.get(`${API}/admin/users?limit=200`, { headers: { "X-Admin-Key": k } }),
        axios.get(`${API}/admin/feedback?limit=200`, { headers: { "X-Admin-Key": k } }),
        axios.get(`${API}/admin/embed/hosts?limit=500`, { headers: { "X-Admin-Key": k } }),
      ]);
      setStats(s.data);
      setVisits(v.data.items || []);
      setUsers(u.data.items || []);
      setFeedback(f.data.items || []);
      setEmbedHosts(eh.data.items || []);
      setAuthed(true);
      sessionStorage.setItem("pl_admin_key", k);
      disableTracking();
    } catch (e) {
      if (e?.response?.status === 401) {
        toast.error("مفتاح الإدارة غير صحيح");
        sessionStorage.removeItem("pl_admin_key");
        setAuthed(false);
      } else {
        toast.error("تعذّر تحميل البيانات");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authed) loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = () => {
    sessionStorage.removeItem("pl_admin_key");
    setKey("");
    setAuthed(false);
    setStats(null);
    setUsers([]);
    setFeedback([]);
    setVisits([]);
    setEmbedHosts([]);
  };

  // --- Mutations
  const togglePin = async (id, next) => {
    try {
      await axios.post(`${API}/admin/feedback/${id}/pin?pinned=${next ? "true" : "false"}`, {}, { headers });
      setFeedback((xs) => xs.map((x) => (x.id === id ? { ...x, pinned: next } : x))
        .sort((a, b) => Number(b.pinned) - Number(a.pinned)));
      toast.success(next ? "تم التثبيت " : "تم إلغاء التثبيت");
    } catch { toast.error("فشل التحديث"); }
  };

  const deleteFeedback = async (id) => {
    if (!window.confirm("حذف هذا التقييم نهائيًا؟")) return;
    try {
      await axios.delete(`${API}/admin/feedback/${id}`, { headers });
      setFeedback((xs) => xs.filter((x) => x.id !== id));
      toast.success("تم الحذف");
    } catch { toast.error("فشل الحذف"); }
  };

  const adjustCredits = async (email, delta) => {
    try {
      const r = await axios.post(`${API}/admin/users/${encodeURIComponent(email)}/credits?delta=${delta}`, {}, { headers });
      setUsers((xs) => xs.map((u) => u.email === email ? { ...u, remaining: r.data.remaining } : u));
      toast.success(`الرصيد الجديد: ${r.data.remaining}`);
    } catch { toast.error("فشل التحديث"); }
  };

  const deleteUser = async (email) => {
    if (!window.confirm(`حذف المستخدم ${email}؟`)) return;
    try {
      await axios.delete(`${API}/admin/users/${encodeURIComponent(email)}`, { headers });
      setUsers((xs) => xs.filter((x) => x.email !== email));
      toast.success("تم حذف المستخدم");
    } catch { toast.error("فشل الحذف"); }
  };

  // --- Redirect-to-login state (keeps the dashboard shell empty while nav happens)
  if (!authed) {
    return (
      <div className="min-h-screen bg-[#182138] text-white font-arabic flex items-center justify-center p-6" dir="rtl">
        <div className="text-center">
          <Shield className="h-8 w-8 text-[#18c98f] mx-auto mb-3" />
          <p className="text-sm text-white/70">جارٍ التحويل لصفحة الدخول…</p>
        </div>
      </div>
    );
  }

  // --- Chart helpers (SVG sparkline)
  const chartMax = Math.max(1, ...visits.map((v) => v.count));
  const chartW = 640;
  const chartH = 160;
  const pts = visits.map((v, i) => {
    const x = (i / Math.max(1, visits.length - 1)) * chartW;
    const y = chartH - (v.count / chartMax) * (chartH - 20) - 10;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(" ");

  const filteredFb = feedback.filter((x) => {
    if (filter === "pinned") return x.pinned;
    if (filter === "with_comments") return (x.comment || "").trim().length > 0;
    return true;
  });

  return (
    <div className="min-h-screen bg-[#182138] text-white font-arabic" dir="rtl">
      <Toaster position="top-center" />

      {/* Header */}
      <header
        className="border-b border-[#2e3a5c] bg-[#1f2a47] sticky top-0 z-20"
        style={{ paddingTop: "env(safe-area-inset-top)" }}
      >
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <Shield className="h-5 w-5 text-[#18c98f]" />
            <h1 className="text-base sm:text-lg font-extrabold">لوحة إدارة برولانسر</h1>
          </div>
          <div className="flex items-center gap-2">
            <a
              href="/strategic-study"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="admin-study-link"
              title="الدراسة الاستراتيجية + جدوى اقتصادية (قابلة للطباعة)"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs bg-[#18c98f]/15 border border-[#18c98f]/40 text-[#18c98f] font-extrabold hover:bg-[#18c98f] hover:text-[#182138]"
            >
              <Sparkles className="h-3.5 w-3.5" />
              الدراسة
            </a>
            <button data-testid="admin-refresh-btn" onClick={() => loadAll()} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs bg-[#182138] border border-[#2e3a5c] hover:border-[#18c98f]/60">
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
              تحديث
            </button>
            <button data-testid="admin-logout-btn" onClick={logout} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs bg-[#182138] border border-[#2e3a5c] hover:border-red-500/60 text-red-300">
              <LogOut className="h-3.5 w-3.5" />
              خروج
            </button>
          </div>
        </div>
        {/* Tabs */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-2 flex items-center gap-1.5 overflow-x-auto no-scrollbar">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-bold border transition shrink-0 ${
                tab === t.id
                  ? "bg-[#18c98f] text-white border-[#18c98f]"
                  : "bg-[#182138] border-[#2e3a5c] text-white/80 hover:border-[#18c98f]/60"
              }`}
            >
              <t.icon className="h-3.5 w-3.5" />
              {t.label}
            </button>
          ))}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {loading && !stats && (
          <div className="rounded-2xl bg-[#1f2a47] border border-[#2e3a5c] p-8 text-center text-white/60 inline-flex items-center justify-center gap-2 w-full">
            <Loader2 className="h-4 w-4 animate-spin" /> جارٍ التحميل…
          </div>
        )}

        {/* Overview */}
        {tab === "overview" && stats && (
          <div className="space-y-6">
            <AdminFunnel adminKey={key} />
            <AdminPackages adminKey={key} />
            {/* SECTION 1 — Real stats (100% authentic) */}
            <section data-testid="overview-real-section">
              <div className="flex items-center gap-2 mb-3">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-[#18c98f] text-white text-[10px] font-black uppercase tracking-widest px-2.5 py-1">
                  <Check className="h-3 w-3" strokeWidth={3} />
                  حقيقي ١٠٠٪
                </span>
                <h2 className="text-sm font-extrabold text-[#18c98f]">الأرقام الحقيقية بعد الإطلاق</h2>
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <StatCard icon={Globe} label="زيارات حقيقية (منذ الإطلاق)" value={fmt(stats.visits.total_real)} highlight />
                <StatCard icon={Eye}   label="زيارات اليوم (حقيقية)" value={fmt(stats.visits.today_real)} highlight />
                <StatCard icon={Users} label="المستخدمون" value={fmt(stats.users.total)} />
                <StatCard icon={Crown} label="مشتركون نشطون" value={fmt(stats.users.subscribers_active)} />
                <StatCard icon={Star}  label="تقييمات حقيقية" value={fmt(stats.feedback.total)} />
                <StatCard icon={Star}  label="متوسط التقييم" value={`${stats.feedback.avg_rating || 0} ⭐`} />
                <StatCard icon={Pin}   label="تقييمات مثبّتة" value={fmt(stats.feedback.pinned)} />
                <StatCard icon={DollarSign} label="عمليات مدفوعة" value={fmt(stats.payments.paid_count)} />
              </div>
              <div className="mt-2 text-[11px] text-white/50">
                تاريخ بدء العدّ: {fmtDateTime(stats.visits.launch_at)}
              </div>
            </section>

            {/* SECTION 2 — Public display (what visitors see on the home page) */}
            <section data-testid="overview-display-section">
              <div className="flex items-center gap-2 mb-3">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-400/15 border border-amber-400/40 text-amber-400 text-[10px] font-black uppercase tracking-widest px-2.5 py-1">
                  عرض عام (مع baseline)
                </span>
                <h2 className="text-sm font-extrabold text-amber-400">الأرقام المعروضة في الفوتر</h2>
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                <StatCard icon={Globe} label="إجمالي معروض (مع baseline)" value={fmt(stats.visits.total_with_baseline)} />
                <StatCard icon={Globe} label="قيمة baseline تسويقي" value={fmt(stats.visits.baseline)} />
                <StatCard icon={Users} label="متصل الآن (محاكاة فقط)" value={`${stats.visits.online_simulated_range[0]}-${stats.visits.online_simulated_range[1]}`} />
              </div>
              <div className="mt-2 text-[11px] text-amber-400/70 leading-relaxed">
                 "متصل الآن" رقم عشوائي تسويقي يُعرض للزوار فقط — لا يُستخدم في أي قرار حقيقي.
              </div>
            </section>

            {/* SECTION 3 — AI usage counters (today + all-time) */}
            {stats.ai && typeof stats.ai.today_total !== "undefined" && (
              <section data-testid="overview-ai-usage-section">
                <div className="flex items-center gap-2 mb-3">
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-[#18c98f] text-white text-[10px] font-black uppercase tracking-widest px-2.5 py-1">
                    <Sparkles className="h-3 w-3" strokeWidth={3} /> AI USAGE
                  </span>
                  <h2 className="text-sm font-extrabold text-[#18c98f]">عدّاد الاقتراحات</h2>
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                  <StatCard icon={Zap}        label="اقتراحات اليوم" value={fmt(stats.ai.today_total || 0)} highlight />
                  <StatCard icon={RefreshCw}  label="استدعاءات Claude اليوم" value={fmt(stats.ai.today_llm_calls || 0)} />
                  <StatCard icon={Sparkles}   label="المجموع الكلي" value={fmt(stats.ai.all_time_total || 0)} />
                  <StatCard icon={DollarSign} label="تكلفة اليوم (ر.س)" value={`${fmt(((stats.ai.today_llm_calls || 0) * 0.03).toFixed(2))} ر.س`} />
                </div>
                <div className="mt-2 text-[11px] text-white/50 leading-relaxed">
                  يشمل العدّاد الاقتراحات المُولَّدة من Claude والمُسترجَعة من الكاش. الكاش = مجاني.
                </div>
              </section>
            )}

            {/* SECTION 4 — AI prompt cache savings */}
            {stats.ai && typeof stats.ai.cache_entries !== "undefined" && (
              <section data-testid="overview-ai-cache-section">
                <div className="flex items-center gap-2 mb-3">
                  <span className="inline-flex items-center gap-1.5 rounded-full bg-[#18c98f]/15 border border-[#18c98f]/40 text-[#18c98f] text-[10px] font-black uppercase tracking-widest px-2.5 py-1">
                    AI CACHE
                  </span>
                  <h2 className="text-sm font-extrabold text-[#18c98f]">توفير تكلفة الذكاء الاصطناعي</h2>
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                  <StatCard icon={DollarSign} label="موفَّر (ريال)" value={`${fmt(stats.ai.cache_saved_sar || 0)} ر.س`} highlight />
                  <StatCard icon={RefreshCw}  label="استدعاءات AI موفّرة" value={fmt(stats.ai.cache_calls_saved || 0)} />
                  <StatCard icon={Check}      label="نتائج مُخزَّنة" value={fmt(stats.ai.cache_entries || 0)} />
                  <StatCard icon={Eye}        label="إجمالي استخدام الكاش" value={fmt(stats.ai.cache_hits_total || 0)} />
                </div>
                <div className="mt-2 text-[11px] text-white/50 leading-relaxed">
                  يُعاد استخدام نفس الاقتراح للطلبات المتطابقة (نفس الخدمة + الخبرة + التعقيد + النطاق) لمدة ٧ أيام — بدون أي استدعاء جديد لـ Claude.
                </div>
              </section>
            )}
          </div>
        )}

        {/* Visits */}
        {tab === "visits" && (
          <div className="space-y-5">
            {stats && (
              <>
                <section data-testid="visits-real-section">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-[#18c98f] text-white text-[10px] font-black uppercase tracking-widest px-2.5 py-1">
                      <Check className="h-3 w-3" strokeWidth={3} /> حقيقي
                    </span>
                    <h2 className="text-sm font-extrabold text-[#18c98f]">الزيارات الحقيقية</h2>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <StatCard icon={Globe} label="إجمالي منذ الإطلاق" value={fmt(stats.visits.total_real)} highlight />
                    <StatCard icon={Eye}   label="اليوم" value={fmt(stats.visits.today_real)} highlight />
                  </div>
                  <div className="mt-1 text-[11px] text-white/50">
                    بدء العدّ: {fmtDateTime(stats.visits.launch_at)}
                  </div>
                </section>
                <section data-testid="visits-display-section">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-400/15 border border-amber-400/40 text-amber-400 text-[10px] font-black uppercase tracking-widest px-2.5 py-1">
                      عرض عام
                    </span>
                    <h2 className="text-sm font-extrabold text-amber-400">الأرقام المعروضة</h2>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <StatCard icon={Globe} label="إجمالي مع baseline" value={fmt(stats.visits.total_with_baseline)} />
                    <StatCard icon={Globe} label="baseline" value={fmt(stats.visits.baseline)} />
                    <StatCard icon={Users} label="متصل الآن (محاكاة)" value={`${stats.visits.online_simulated_range[0]}-${stats.visits.online_simulated_range[1]}`} />
                  </div>
                </section>
              </>
            )}
            <div className="rounded-2xl bg-[#1f2a47] border border-[#2e3a5c] p-4">
              <h3 className="text-sm font-extrabold mb-3">آخر ٣٠ يومًا (حقيقي)</h3>
              {visits.length === 0 ? (
                <div className="text-white/50 text-sm text-center py-8">لا توجد بيانات بعد.</div>
              ) : (
                <div className="relative">
                  <svg viewBox={`0 0 ${chartW} ${chartH}`} className="w-full h-32 sm:h-40" preserveAspectRatio="none">
                    <defs>
                      <linearGradient id="g1" x1="0" x2="0" y1="0" y2="1">
                        <stop offset="0%" stopColor="#18c98f" stopOpacity="0.5" />
                        <stop offset="100%" stopColor="#18c98f" stopOpacity="0" />
                      </linearGradient>
                    </defs>
                    <polyline fill="none" stroke="#18c98f" strokeWidth="2" points={pts} />
                    <polyline fill="url(#g1)" stroke="none" points={`0,${chartH} ${pts} ${chartW},${chartH}`} />
                  </svg>
                </div>
              )}
              <ul className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                {visits.slice(-8).map((d) => (
                  <li key={d.day} className="rounded-xl bg-[#182138] border border-[#2e3a5c] px-3 py-2 flex items-center justify-between">
                    <span className="text-white/60">{d.day}</span>
                    <span className="font-mono-num font-extrabold text-[#18c98f]">{fmt(d.count)}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {/* Users */}
        {tab === "users" && (
          <div className="rounded-2xl bg-[#1f2a47] border border-[#2e3a5c] overflow-hidden">
            <div className="p-4 flex items-center justify-between">
              <h3 className="text-sm font-extrabold">المستخدمون ({users.length})</h3>
            </div>
            {users.length === 0 ? (
              <div className="text-white/50 text-sm text-center py-10">لا يوجد مستخدمون بعد.</div>
            ) : (
              <ul className="divide-y divide-[#2e3a5c]">
                {users.map((u) => (
                  <li key={u.email} data-testid={`admin-user-${u.email}`} className="p-4 flex items-start gap-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-bold" dir="ltr">{u.email}</span>
                        {u.sub_active && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-[#18c98f] text-white text-[10px] font-extrabold px-2 py-0.5">
                            <Crown className="h-3 w-3" />
                            {u.sub_package_id || "subscriber"}
                          </span>
                        )}
                        {u.bonus_claimed_at && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-[#18c98f]/15 text-[#18c98f] text-[10px] font-extrabold px-2 py-0.5">
                            <Check className="h-3 w-3" /> هدية
                          </span>
                        )}
                      </div>
                      <div className="mt-1 text-xs text-white/60 flex gap-4 flex-wrap">
                        <span>الاسم: <b className="text-white/85">{u.name || "—"}</b></span>
                        <span>الرصيد: <b className="text-[#18c98f]">{fmt(u.remaining)}</b></span>
                        <span>آخر تحديث: {fmtDateTime(u.updated_at)}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <button data-testid={`admin-credit-add-${u.email}`} onClick={() => adjustCredits(u.email, 5)} title="+5 رصيد" className="p-2 rounded-full bg-[#182138] border border-[#2e3a5c] text-[#18c98f] hover:border-[#18c98f]">
                        <Plus className="h-3.5 w-3.5" />
                      </button>
                      <button data-testid={`admin-credit-sub-${u.email}`} onClick={() => adjustCredits(u.email, -1)} title="-1 رصيد" className="p-2 rounded-full bg-[#182138] border border-[#2e3a5c] text-amber-400 hover:border-amber-500">
                        <Minus className="h-3.5 w-3.5" />
                      </button>
                      <button data-testid={`admin-user-delete-${u.email}`} onClick={() => deleteUser(u.email)} title="حذف المستخدم" className="p-2 rounded-full bg-[#182138] border border-[#2e3a5c] text-red-400 hover:border-red-500">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Reviews */}
        {tab === "reviews" && (
          <div>
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              {[
                { k: "all", label: "الكل" },
                { k: "pinned", label: "المثبّتة فقط" },
                { k: "with_comments", label: "التي بها تعليق" },
              ].map((f) => (
                <button
                  key={f.k}
                  data-testid={`admin-filter-${f.k}`}
                  onClick={() => setFilter(f.k)}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold border transition ${
                    filter === f.k
                      ? "bg-[#18c98f] text-white border-[#18c98f]"
                      : "bg-[#1f2a47] border-[#2e3a5c] text-white/80 hover:border-[#18c98f]/60"
                  }`}
                >
                  {f.label}
                </button>
              ))}
              <span className="text-xs text-white/50">({filteredFb.length})</span>
            </div>
            {filteredFb.length === 0 ? (
              <div className="rounded-2xl bg-[#1f2a47] border border-[#2e3a5c] p-8 text-center text-white/60">لا توجد تقييمات.</div>
            ) : (
              <ul className="space-y-3">
                {filteredFb.map((x) => (
                  <li key={x.id} data-testid={`admin-feedback-${x.id}`} className={`rounded-2xl p-4 border flex items-start gap-3 ${x.pinned ? "bg-[#18c98f]/8 border-[#18c98f]/40" : "bg-[#1f2a47] border-[#2e3a5c]"}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        {[1, 2, 3, 4, 5].map((n) => (
                          <Star key={n} className={`h-4 w-4 ${n <= x.rating ? "fill-[#18c98f] text-[#18c98f]" : "text-white/20"}`} strokeWidth={1.5} />
                        ))}
                        {x.pinned && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-[#18c98f] text-white text-[10px] font-extrabold px-2 py-0.5">
                            <Pin className="h-3 w-3" /> مُثبَّت
                          </span>
                        )}
                        <span className="text-[11px] text-white/50 ms-auto">{fmtDateTime(x.created_at)}</span>
                      </div>
                      <div className="mt-2 text-sm text-white/90 leading-relaxed break-words">
                        {x.comment || <span className="text-white/40">— بدون تعليق —</span>}
                      </div>
                      <div className="mt-2 flex items-center gap-3 text-[11px] text-white/55 flex-wrap">
                        <span>الاسم: <b className="text-white/80">{x.name || "زائر"}</b></span>
                        {x.email && <span dir="ltr">بريد: <b className="text-white/80">{x.email}</b></span>}
                        {x.context && <span>السياق: <b className="text-white/80">{x.context}</b></span>}
                        {x.service_type && <span>الخدمة: <b className="text-white/80">{x.service_type}</b></span>}
                      </div>
                    </div>
                    <div className="flex flex-col gap-1.5 shrink-0">
                      <button
                        data-testid={`admin-pin-btn-${x.id}`}
                        onClick={() => togglePin(x.id, !x.pinned)}
                        title={x.pinned ? "إلغاء التثبيت" : "تثبيت"}
                        className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-extrabold ${
                          x.pinned ? "bg-[#18c98f] text-white hover:brightness-110" : "bg-[#2e3a5c] text-white hover:bg-[#18c98f] hover:text-[#182138]"
                        }`}
                      >
                        {x.pinned ? <PinOff className="h-3.5 w-3.5" /> : <Pin className="h-3.5 w-3.5" />}
                        {x.pinned ? "إلغاء" : "تثبيت"}
                      </button>
                      <button
                        data-testid={`admin-del-btn-${x.id}`}
                        onClick={() => deleteFeedback(x.id)}
                        title="حذف نهائي"
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-bold bg-[#182138] text-red-400 border border-[#2e3a5c] hover:border-red-500"
                      >
                        <Trash2 className="h-3.5 w-3.5" /> حذف
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Embed hosts — third-party websites embedding the widget */}
        {tab === "embeds" && (
          <div className="rounded-2xl bg-[#1f2a47] border border-[#2e3a5c] overflow-hidden">
            <div className="p-4 flex items-center justify-between flex-wrap gap-2">
              <div>
                <h3 className="text-sm font-extrabold">مواقع التضمين ({embedHosts.length})</h3>
                <p className="text-xs text-white/60 mt-1">
                  النطاقات التي تستضيف حاسبة برولانسر عبر iframe. استخدم <b className="text-amber-400">الدرع 🛡️</b> لحظر الموقع (ستظهر رسالة إيقاف للزوار)، أو <b className="text-red-400">السلة 🗑️</b> لحذف التسجيل فقط.
                </p>
              </div>
            </div>
            {embedHosts.length === 0 ? (
              <div className="text-white/50 text-sm text-center py-10">
                لم يُسجَّل أي موقع تضمين بعد. سيظهر هنا كل موقع يستخدم الـ embed تلقائيًا.
              </div>
            ) : (
              <ul className="divide-y divide-[#2e3a5c]">
                {embedHosts.map((h) => (
                  <li key={h.host} data-testid={`admin-embed-${h.host}`} className={`p-4 flex items-start gap-3 flex-wrap ${h.blocked ? "bg-red-500/5" : ""}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Globe className={`h-4 w-4 shrink-0 ${h.blocked ? "text-red-400" : "text-[#18c98f]"}`} />
                        <a
                          href={`https://${h.host}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          dir="ltr"
                          className={`font-extrabold hover:underline break-all ${h.blocked ? "text-red-300 line-through" : "text-white hover:text-[#18c98f]"}`}
                        >
                          {h.host}
                        </a>
                        {h.blocked ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-red-500 text-white text-[10px] font-black px-2 py-0.5">
                            محظور
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 rounded-full bg-[#18c98f]/15 text-[#18c98f] text-[10px] font-extrabold px-2 py-0.5">
                            {fmt(h.views)} مشاهدة
                          </span>
                        )}
                      </div>
                      <div className="mt-1 text-xs text-white/60 flex gap-4 flex-wrap">
                        <span>أول ظهور: {fmtDateTime(h.first_seen)}</span>
                        <span>آخر ظهور: {fmtDateTime(h.last_seen)}</span>
                        {h.last_lang && <span>اللغة: <b className="text-white/85">{h.last_lang}</b></span>}
                        {h.last_ip && <span dir="ltr">IP: <b className="text-white/85">{h.last_ip}</b></span>}
                      </div>
                      {h.last_referrer && (
                        <div className="mt-1 text-[11px] text-white/40 break-all" dir="ltr">
                          مرجع: {h.last_referrer}
                        </div>
                      )}
                      {h.last_path && (
                        <div className="mt-1 text-[11px] text-white/40 break-all" dir="ltr">
                          المسار: {h.last_path}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <a
                        href={`https://${h.host}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="فتح الموقع"
                        className="p-2 rounded-full bg-[#182138] border border-[#2e3a5c] text-[#18c98f] hover:border-[#18c98f]"
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                      <button
                        data-testid={`admin-embed-block-${h.host}`}
                        onClick={() => toggleEmbedBlock(h.host, !h.blocked)}
                        title={h.blocked ? "إلغاء الحظر" : "حظر الموقع"}
                        className={`p-2 rounded-full border transition ${h.blocked ? "bg-[#18c98f] text-white border-[#18c98f] hover:brightness-110" : "bg-[#182138] border-[#2e3a5c] text-amber-400 hover:border-amber-500"}`}
                      >
                        <Shield className="h-3.5 w-3.5" />
                      </button>
                      <button
                        data-testid={`admin-embed-delete-${h.host}`}
                        onClick={() => deleteEmbedHost(h.host)}
                        title="حذف التسجيل"
                        className="p-2 rounded-full bg-[#182138] border border-[#2e3a5c] text-red-400 hover:border-red-500"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* ===== AI tab ===== */}
        {tab === "ai" && (
          <div className="space-y-5" data-testid="ai-dashboard">
            {aiLoading && !aiOverview && (
              <div className="rounded-2xl bg-[#1f2a47] border border-[#2e3a5c] p-6 text-center text-white/70">
                <Loader2 className="h-5 w-5 animate-spin inline me-2" /> جاري التحميل...
              </div>
            )}

            {aiOverview && (
              <>
                {/* Headline KPIs */}
                <section>
                  <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-[#18c98f] text-white text-[10px] font-black uppercase tracking-widest px-2.5 py-1">
                        <Sparkles className="h-3 w-3" strokeWidth={3} /> AI LIVE
                      </span>
                      <h2 className="text-sm font-extrabold text-[#18c98f]">استخدام الذكاء الاصطناعي اليوم</h2>
                    </div>
                    <button
                      onClick={loadAI}
                      className="inline-flex items-center gap-1.5 rounded-full bg-[#182138] border border-[#2e3a5c] px-3 py-1.5 text-xs font-bold text-white/80 hover:border-[#18c98f]"
                    >
                      <RefreshCw className="h-3 w-3" /> تحديث
                    </button>
                  </div>
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    <StatCard icon={DollarSign} label="تكلفة اليوم (ر.س)" value={`${fmt(aiOverview.today.spend_sar)} ر.س`} highlight />
                    <StatCard icon={Zap}        label="استدعاءات Claude اليوم" value={fmt(aiOverview.today.llm_calls)} />
                    <StatCard icon={Check}      label="موفَّر من الكاش اليوم" value={`${fmt(aiOverview.today.saved_sar)} ر.س`} />
                    <StatCard icon={Eye}        label="نسبة إصابة الكاش" value={`${aiOverview.today.cache_hit_ratio}%`} />
                  </div>
                  {/* Budget bar */}
                  <div className="mt-3 rounded-2xl bg-[#182138] border border-[#2e3a5c] p-4">
                    <div className="flex items-center justify-between text-xs flex-wrap gap-2">
                      <span className="text-white/70">
                        الميزانية اليومية: <b className="font-mono-num text-[#18c98f]">{fmt(aiOverview.today.spend_sar)}</b>
                        <span className="text-white/40"> / </span>
                        <b className="font-mono-num">{fmt(aiOverview.today.cap_sar)}</b> ر.س
                      </span>
                      <span className={`font-mono-num font-extrabold ${aiOverview.today.cap_used_pct >= 90 ? "text-red-400" : aiOverview.today.cap_used_pct >= 60 ? "text-amber-400" : "text-[#18c98f]"}`}>
                        {aiOverview.today.cap_used_pct}%
                      </span>
                    </div>
                    <div className="mt-2 h-2 rounded-full bg-[#2e3a5c] overflow-hidden">
                      <div
                        className={`h-full transition-all ${aiOverview.today.cap_used_pct >= 90 ? "bg-red-500" : aiOverview.today.cap_used_pct >= 60 ? "bg-amber-400" : "bg-[#18c98f]"}`}
                        style={{ width: `${Math.min(100, aiOverview.today.cap_used_pct)}%` }}
                      />
                    </div>
                    {aiOverview.today.cap_used_pct >= 90 && (
                      <div className="mt-2 text-[11px] text-red-400 flex items-center gap-1">
                        <AlertTriangle className="h-3 w-3" /> اقتربت من بلوغ الميزانية — سيُوقَف الـ AI تلقائيًا عند 100%.
                      </div>
                    )}
                  </div>
                </section>

                {/* Settings controls */}
                <section>
                  <h3 className="text-sm font-extrabold mb-3 text-white/90">الإعدادات</h3>
                  <div className="rounded-2xl bg-[#1f2a47] border border-[#2e3a5c] p-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Kill switch */}
                    <div className="flex items-center justify-between gap-2 rounded-xl bg-[#182138] border border-[#2e3a5c] p-3">
                      <div>
                        <div className="text-xs text-white/60">خدمة AI</div>
                        <div className={`mt-0.5 text-sm font-extrabold ${aiOverview.settings.enabled ? "text-[#18c98f]" : "text-red-400"}`}>
                          {aiOverview.settings.enabled ? "تعمل" : "متوقفة"}
                        </div>
                      </div>
                      <button
                        data-testid="ai-toggle"
                        onClick={() => saveAiSettings({ enabled: !aiOverview.settings.enabled })}
                        className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-extrabold ${aiOverview.settings.enabled ? "bg-red-500/15 text-red-400 border border-red-500/40 hover:bg-red-500 hover:text-white" : "bg-[#18c98f] text-white hover:brightness-110"}`}
                      >
                        <Power className="h-3.5 w-3.5" />
                        {aiOverview.settings.enabled ? "إيقاف" : "تشغيل"}
                      </button>
                    </div>

                    {/* Daily cap */}
                    <SettingField
                      testId="ai-daily-cap"
                      label="السقف اليومي (ر.س)"
                      hint="عند بلوغه يتوقف AI تلقائيًا حتى اليوم التالي"
                      value={aiOverview.settings.daily_cap_sar}
                      step={5}
                      onSave={(v) => saveAiSettings({ daily_cap_sar: v })}
                    />
                    {/* Cost per call */}
                    <SettingField
                      testId="ai-cost-per-call"
                      label="تكلفة الاستدعاء (ر.س)"
                      hint="القيمة التقديرية لكل استدعاء Claude"
                      value={aiOverview.settings.cost_per_call_sar}
                      step={0.005}
                      onSave={(v) => saveAiSettings({ cost_per_call_sar: v })}
                    />
                  </div>
                </section>

                {/* Period totals */}
                <section>
                  <h3 className="text-sm font-extrabold mb-3 text-white/90">ملخّص فترات</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <PeriodCard title="آخر ٧ أيام" bucket={aiOverview.last_7d} />
                    <PeriodCard title="آخر ٣٠ يوم" bucket={aiOverview.last_30d} />
                    <PeriodCard title="منذ الإطلاق" bucket={aiOverview.all_time} />
                  </div>
                </section>

                {/* Daily chart */}
                {aiUsageDays.length > 0 && (
                  <section>
                    <h3 className="text-sm font-extrabold mb-3 text-white/90">استخدام يومي (آخر ٣٠ يوم)</h3>
                    <div className="rounded-2xl bg-[#1f2a47] border border-[#2e3a5c] p-4">
                      <ul className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                        {aiUsageDays.slice(-8).map((d) => (
                          <li key={d.day} className="rounded-xl bg-[#182138] border border-[#2e3a5c] px-3 py-2">
                            <div className="text-white/50 text-[10px]">{d.day}</div>
                            <div className="mt-0.5 flex items-center justify-between">
                              <span className="font-mono-num text-[#18c98f] font-extrabold">{fmt(d.llm_calls)}</span>
                              <span className="font-mono-num text-white/40">{fmt(d.spend_sar)} ر.س</span>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </section>
                )}

                {/* Cache management */}
                <section>
                  <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                    <h3 className="text-sm font-extrabold text-white/90">ذاكرة النتائج (آخر {aiCache.length})</h3>
                    <button
                      onClick={clearAiCache}
                      data-testid="ai-cache-clear"
                      className="inline-flex items-center gap-1.5 rounded-full bg-red-500/15 border border-red-500/40 text-red-400 px-3 py-1.5 text-xs font-extrabold hover:bg-red-500 hover:text-white"
                    >
                      <Trash2 className="h-3.5 w-3.5" /> مسح الكل
                    </button>
                  </div>
                  {aiCache.length === 0 ? (
                    <div className="rounded-2xl bg-[#1f2a47] border border-[#2e3a5c] p-6 text-center text-white/50 text-sm">
                      لا توجد نتائج محفوظة.
                    </div>
                  ) : (
                    <ul className="rounded-2xl bg-[#1f2a47] border border-[#2e3a5c] divide-y divide-[#2e3a5c]">
                      {aiCache.map((c) => (
                        <li key={c.id} className="p-3 flex items-center justify-between gap-3 flex-wrap">
                          <div className="flex-1 min-w-0 text-xs">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="inline-flex items-center gap-1 rounded-full bg-[#18c98f]/15 text-[#18c98f] text-[10px] font-extrabold px-2 py-0.5">
                                {c.service} · {c.experience} · {c.complexity}
                              </span>
                              <span className="inline-flex items-center gap-1 rounded-full bg-[#182138] border border-[#2e3a5c] text-white/70 text-[10px] font-bold px-2 py-0.5">
                                {c.hits} مشاهدة
                              </span>
                              <span className="text-white/50 font-mono-num">
                                موصى: {c.recommended_price} / فوز: {c.win_rate_price}
                              </span>
                            </div>
                            <div className="mt-1 text-[10px] text-white/40">
                              آخر استخدام: {fmtDateTime(c.last_hit)}
                            </div>
                          </div>
                          <button
                            onClick={() => deleteCacheEntry(c.id)}
                            title="حذف هذه النتيجة"
                            className="p-2 rounded-full bg-[#182138] border border-[#2e3a5c] text-red-400 hover:border-red-500"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>

                {/* Recent calls */}
                {aiUsageList.length > 0 && (
                  <section>
                    <h3 className="text-sm font-extrabold mb-3 text-white/90">آخر العمليات</h3>
                    <ul className="rounded-2xl bg-[#1f2a47] border border-[#2e3a5c] divide-y divide-[#2e3a5c]">
                      {aiUsageList.map((u, idx) => (
                        <li key={idx} className="p-3 flex items-center justify-between gap-3 text-xs">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={`inline-flex items-center gap-1 rounded-full text-[10px] font-extrabold px-2 py-0.5 ${u.cache_hit ? "bg-[#18c98f]/15 text-[#18c98f]" : "bg-amber-400/15 text-amber-400"}`}>
                              {u.cache_hit ? "من الكاش" : "استدعاء جديد"}
                            </span>
                            <span className="text-white/80">{u.service || "—"}</span>
                            <span className="text-white/40">· {u.experience || "—"}</span>
                            {u.email && <span dir="ltr" className="text-white/50">· {u.email}</span>}
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="font-mono-num text-white/60">{fmt(u.cost_sar || 0)} ر.س</span>
                            <span className="text-[10px] text-white/40">{fmtDateTime(u.ts)}</span>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </section>
                )}
              </>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, highlight = false }) {
  return (
    <div
      className={`rounded-2xl p-4 border ${
        highlight
          ? "bg-[#18c98f]/10 border-[#18c98f]/40"
          : "bg-[#1f2a47] border-[#2e3a5c]"
      }`}
    >
      <div className="flex items-center gap-2 text-[10px] text-white/60 uppercase tracking-wider">
        <Icon className="h-3.5 w-3.5 text-[#18c98f]" />
        {label}
      </div>
      <div className="mt-2 text-xl sm:text-2xl font-extrabold text-[#18c98f] font-mono-num tabular-nums">
        {value}
      </div>
    </div>
  );
}

function SettingField({ label, hint, value, step = 1, onSave, testId }) {
  const [v, setV] = useState(value);
  useEffect(() => { setV(value); }, [value]);
  const dirty = Number(v) !== Number(value);
  return (
    <div className="rounded-xl bg-[#182138] border border-[#2e3a5c] p-3">
      <div className="text-xs text-white/60">{label}</div>
      {hint && <div className="text-[10px] text-white/40 mt-0.5">{hint}</div>}
      <div className="mt-2 flex items-center gap-2">
        <input
          type="number"
          step={step}
          min={0}
          value={v}
          data-testid={testId}
          onChange={(e) => setV(e.target.value)}
          className="w-full h-9 rounded-lg bg-[#1f2a47] border border-[#2e3a5c] px-3 text-sm font-mono-num font-bold text-white focus:border-[#18c98f] outline-none"
          dir="ltr"
        />
        <button
          disabled={!dirty || v === "" || isNaN(Number(v))}
          onClick={() => onSave(Number(v))}
          className={`shrink-0 px-3 h-9 rounded-full text-xs font-extrabold ${dirty && !isNaN(Number(v)) ? "bg-[#18c98f] text-white hover:brightness-110" : "bg-[#2e3a5c] text-white/40 cursor-not-allowed"}`}
        >
          حفظ
        </button>
      </div>
    </div>
  );
}

function PeriodCard({ title, bucket }) {
  return (
    <div className="rounded-2xl bg-[#1f2a47] border border-[#2e3a5c] p-4">
      <div className="text-[10px] uppercase tracking-widest text-[#18c98f] font-black">{title}</div>
      <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
        <div>
          <div className="text-white/50">استدعاءات</div>
          <div className="mt-0.5 font-mono-num text-base font-extrabold text-white">{(bucket?.llm_calls ?? 0).toLocaleString("ar-SA")}</div>
        </div>
        <div>
          <div className="text-white/50">كلفة</div>
          <div className="mt-0.5 font-mono-num text-base font-extrabold text-amber-400">{(bucket?.spend_sar ?? 0).toLocaleString("ar-SA")} ر.س</div>
        </div>
        <div>
          <div className="text-white/50">من الكاش</div>
          <div className="mt-0.5 font-mono-num text-base font-extrabold text-white">{(bucket?.cache_hits ?? 0).toLocaleString("ar-SA")}</div>
        </div>
        <div>
          <div className="text-white/50">موفَّر</div>
          <div className="mt-0.5 font-mono-num text-base font-extrabold text-[#18c98f]">{(bucket?.saved_sar ?? 0).toLocaleString("ar-SA")} ر.س</div>
        </div>
      </div>
    </div>
  );
}

